/**
 * Sends GA4 `view_promotion` and `select_promotion` for the merchandising links
 * on the homepage — the hero tiles and the category cards.
 *
 * Both halves live here because they read the same attribute contract, written
 * in Liquid next to the markup it describes (sections/hero-behory.liquid,
 * sections/category-tiles-behory.liquid):
 *
 *   data-bh-promo       where it leads  -> promotion_id
 *   data-bh-promo-name  label           -> promotion_name
 *   data-bh-promo-slot  section:pos     -> creative_slot
 *   href                                -> location_id
 *
 * Impressions are tracked as well as clicks, because clicks alone rank by how
 * far up the page something sits: a tile below the fold loses to one above it
 * before anyone has decided anything. With both, GA4 can divide.
 *
 * Sent through the Google channel's gtag — see bh-gtag.js for how, and for what
 * that means for consent.
 *
 * `location_id` holds the destination URL: it is the only standard string
 * parameter left, and a custom one would need a custom dimension registered in
 * GA4 before it showed up in any report.
 *
 * Same shape as bh-select-item.js: one delegated capture-phase listener, no
 * per-element wiring, nothing to re-bind when a section re-renders.
 */

import { sendEvent } from '@theme/bh-gtag';

const PROMO_SELECTOR = '[data-bh-promo]';

// Views are batched: nine tiles above the fold are one event, not nine.
const FLUSH_DELAY = 300;

// How much of a promotion has to be on screen before it counts as seen. The IAB
// viewability standard asks for half of a normal creative but only 30% of a
// large one, and that exception is the whole reason it exists: a banner big
// enough to fill most of the window can be unmistakably on screen and still
// miss a 50% bar. The hero cards are 543x725 — on a laptop-height window they
// sat at 42% with nothing above them but the headline, so a flat 50% only
// counted them after a scroll. Category cards stay under the cutoff and keep
// the stricter bar.
const LARGE_CREATIVE_AREA = 242500;
const VIEW_RATIO = 0.5;
const VIEW_RATIO_LARGE = 0.3;

/**
 * @param {DOMRectReadOnly} rect
 * @returns {number}
 */
function viewRatioFor(rect) {
  return rect.width * rect.height >= LARGE_CREATIVE_AREA ? VIEW_RATIO_LARGE : VIEW_RATIO;
}

/**
 * The element's attributes in GA4's promotion vocabulary. GA4 reads these per
 * item, so both events carry them inside `items`.
 *
 * @param {Element} element
 * @returns {Record<string, string | undefined>}
 */
function promotion(element) {
  const data = /** @type {HTMLElement} */ (element).dataset;

  return {
    promotion_id: data.bhPromo,
    promotion_name: data.bhPromoName || undefined,
    creative_slot: data.bhPromoSlot || undefined,
    location_id: element.getAttribute('href') || undefined,
  };
}

document.addEventListener(
  'click',
  (event) => {
    if (window.Shopify?.designMode) return;
    if (!(event.target instanceof Element)) return;

    const element = event.target.closest(PROMO_SELECTOR);
    if (!element) return;

    // No interactive-element guard, unlike bh-select-item.js: the tagged
    // element is the link itself, so every click inside it is a click on it.
    const item = promotion(element);

    // Both scopes on purpose: the event-level params are what DebugView and the
    // realtime card read, the item-scoped ones are what the Promotions report
    // aggregates.
    sendEvent('select_promotion', { ...item, items: [item] });
  },
  { capture: true }
);

if (!window.Shopify?.designMode && 'IntersectionObserver' in window) {
  /** @type {Record<string, string | undefined>[]} */
  const pending = [];
  /** @type {number | undefined} */
  let flushTimer;

  function flush() {
    flushTimer = undefined;
    if (pending.length === 0) return;

    sendEvent('view_promotion', { items: pending.splice(0) });
  }

  // A tile counts once per page view: scrolling a banner past twice is one
  // impression, not two. `unobserve` is what enforces that — no set of seen
  // elements to keep. The threshold list only decides when the callback runs;
  // `viewRatioFor` decides whether the entry actually clears the bar, because
  // the bar depends on how big the creative is.
  const observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.intersectionRatio < viewRatioFor(entry.boundingClientRect)) continue;

        observer.unobserve(entry.target);
        pending.push(promotion(entry.target));
      }

      if (pending.length > 0 && flushTimer == null) flushTimer = setTimeout(flush, FLUSH_DELAY);
    },
    { threshold: [VIEW_RATIO_LARGE, VIEW_RATIO] }
  );

  const observed = new WeakSet();

  function scan() {
    for (const element of document.querySelectorAll(PROMO_SELECTOR)) {
      if (observed.has(element)) continue;

      observed.add(element);
      observer.observe(element);
    }
  }

  scan();

  // A section re-rendered through the Section Rendering API brings new nodes
  // with it. Cheap to re-scan, and the WeakSet keeps it idempotent.
  try {
    let scheduled = false;

    new MutationObserver(() => {
      if (scheduled) return;

      scheduled = true;
      requestAnimationFrame(() => {
        scheduled = false;
        scan();
      });
    }).observe(document.body, { childList: true, subtree: true });
  } catch {
    // No MutationObserver: the first scan still covers the page as loaded.
  }
}
